import { APP_COMPANY } from './_Company/_Company';
import { APP_COMPANY_CONFIG_LS } from './_CompanyLS/AppCompanyConfigLS';
import { APP_BRAND_SLOTS } from './_Brand/AppBrandSlots';
import { APP_LOAD_DATA_MODE } from './AppConfig.Mode';
import { CONFIG_PLUGIN } from "./Plugins/_Config.Plugin";
import { APP_MODULES_GET_PUBLIC_ROUTES, APP_MODULES_GET_PRIVATE_ROUTES } from '../../Web/Views/AppModules';
import { SPANISH } from './_Translate/Spanish';
import { ENGLISH } from './_Translate/English';

const SHELL_TRANSLATE = { es: SPANISH, en: ENGLISH };

export const onShellContent = (lang = 'es') => (SHELL_TRANSLATE[lang] ?? SPANISH);

/*
    🔥 SHELL:  Header, footer, sidebar y routing leen TODOS de acá.

    De dónde salen las rutas:
        IS_ENABLED: true  -> del SEED (AppLoadDataMode)
        IS_ENABLED: false -> de APP_MODULES (ya conectado)
*/

/*
    ---------------------------- 🔥 LOS scrollToItems SE VUELVEN SUBMENÚ

    ⚠️ NavList sólo sabe dibujar item.children. Los módulos declaran los anclas
       de sección como scrollToItems, así que si no se traducen no aparecen: era
       lo que pasaba con los ítems de Servicios, Clientes, Proyectos, Nosotros y
       Contacto del Home.

    ⚠️ Cada hijo lleva hash. NavList lo usa para hacer scroll suave a la sección
       en vez de navegar, porque son anclas dentro de la misma página.
*/
const onBuildScrollChildren = (items) => items.map((item) => ({
    ...item,
    children: (item.scrollToItems ?? []).map((section) => ({
        path: `${item.path}#${section.id}`,
        hash: section.id,
        label: section.label,
        parentPath: item.path,
    })),
}));

// ---------------------------- 🔥 RESOLVER DE ORIGEN
const onResolveNavigation = (seedBlock, moduleResolver, dataUser, lang = 'es') => {

    if (seedBlock?.IS_ENABLED) return onBuildScrollChildren(seedBlock.DATASET ?? []);

    if (typeof moduleResolver === 'function') return onBuildScrollChildren(moduleResolver(dataUser, lang) ?? []);

    return []; // reservado para el módulo
};

/*
    ---------------------------- 🔥 ORIGEN ACTIVO

    ⚠️ ESTE ARCHIVO SE EVALÚA UNA SOLA VEZ, AL IMPORTARSE

    Acá todavía no hay sesión: se resuelve con dataUser en null. Alcanza
    para el header y el footer, que sólo necesitan rutas y etiquetas.

    Para lo que SÍ depende del usuario —permisos, roles, o el
    scrollToItems que tu HomeModule arma según isServerDown— está
    onResolveShellNav(dataUser) más abajo, que se llama DENTRO del
    componente, cuando la sesión ya existe.
*/
const NAV_PUBLIC = onResolveNavigation(APP_LOAD_DATA_MODE.LOAD_SEED_PUBLIC_NAVIGATION, APP_MODULES_GET_PUBLIC_ROUTES, null);
const NAV_PRIVATE = onResolveNavigation(APP_LOAD_DATA_MODE.LOAD_SEED_PRIVATE_NAVIGATION, APP_MODULES_GET_PRIVATE_ROUTES, null);

// ---------------------------- 🔥 DERIVADOS
const onBuildFooterColumns = (items, defaultGroup = 'Navegación') => {

    const GROUPED = items.filter((item) => item.displayInFooter).reduce((acc, item) => {
        const KEY = item.footerGroup ?? defaultGroup;
        acc[KEY] = acc[KEY] ?? [];
        acc[KEY].push({ path: item.path, label: item.label });
        return acc;
    }, {});

    return Object.entries(GROUPED).map(([title, items]) => ({ title, items }));
};

const IS_SEED_ACTIVE = (
    APP_LOAD_DATA_MODE.LOAD_SEED_PUBLIC_NAVIGATION?.IS_ENABLED ||
    APP_LOAD_DATA_MODE.LOAD_SEED_PRIVATE_NAVIGATION?.IS_ENABLED
);

// ---------------------------- 🔥 EXPORT
/*
    La construccion visual del brand vive en _Brand/AppBrandSlots.js.
    Se mantiene fuera de este archivo para evitar ciclos con AppModules.
*/

export const APP_SHELL_MENU = {

    brand: {
        name: APP_COMPANY.COMPANY_NAME,
        claim: APP_COMPANY.COMPANY_CLAIM,
        ...APP_BRAND_SLOTS,
    },

    // Rutas completas (para el routing)
    publicRoutes: NAV_PUBLIC,
    privateRoutes: NAV_PRIVATE,

    // Filtrados (para header y sidebar)
    publicNav: NAV_PUBLIC.filter((item) => item.displayInHeader),
    privateNav: NAV_PRIVATE,

    footerColumns: onBuildFooterColumns(NAV_PUBLIC),

    /*
        🔥 UTILIDADES DEL HEADER
        El orden de este array es el orden en que se dibujan.
        requiresAuth: true -> solo con sesión iniciada.
    */
    utilities: [
        { id: 'carts', isEnabled: CONFIG_PLUGIN?.CONTEXTS?.HAS__CART_CONTEXT ?? false, requiresAuth: false },
        { id: 'favs', isEnabled: CONFIG_PLUGIN?.CONTEXTS?.HAS__FAVS_CONTEXT ?? false, requiresAuth: false },
        { id: 'notifications', isEnabled: CONFIG_PLUGIN?.CONTEXTS?.HAS__NOTIFY_CONTEXT ?? false, requiresAuth: true },
        { id: 'messages', isEnabled: CONFIG_PLUGIN?.CONTEXTS?.HAS__CHAT_CONVERSATION_CONTEXT ?? false, requiresAuth: true },
        { id: 'messages_email', isEnabled: CONFIG_PLUGIN?.CONTEXTS?.HAS__EMAIL_CHAT_CONVERSATION_CONTEXT ?? false, requiresAuth: true },
        { id: 'messages_whatsapp', isEnabled: CONFIG_PLUGIN?.CONTEXTS?.HAS__WHATSAPP_CONTEXT ?? false, requiresAuth: true },
        { id: 'language', isEnabled: false, requiresAuth: false }, // Portfolio: traduccion activa internamente, selector oculto
        { id: 'theme', isEnabled: false, requiresAuth: false }, // Portfolio dark-only
        { id: 'profile', isEnabled: true, requiresAuth: false },
        { id: 'auth_portal', isEnabled: CONFIG_PLUGIN?.SCREENS?.HAS__SCREEN_PORTAL_LOGIN ?? false, requiresAuth: false, hideWhenAuth: true },
    ],

    // 🔥 A dónde lleva "Ver todos" de cada utilidad. null = no se muestra el botón.
    utilityLinks: {
        notifications: '/dashboard',
        messages: '/comunicacion/mensajes',          // conversación · chat interno del equipo
        messages_email: '/comunicacion/correo',      // sobre · bandeja de correo
        messages_whatsapp: '/comunicacion/whatsapp', // WhatsApp · clientes por la API Business
        favs: '/dashboard',
        carts: '/dashboard',
        auth_portal: '/auth/portal/login',
    },

    // 🔥 Texto del botón de acceso al portal, en el header.
    portalLabel: SPANISH.TEMPLATES.header.portal,

    profileLinks: [
        { path: '/dashboard', label: SPANISH.TEMPLATES.header.myPanel },
        { path: '/dashboard/perfil', label: SPANISH.TEMPLATES.header.myProfile },
    ],

    languages: [
        { code: 'es', label: 'Español' },
        { code: 'en', label: 'English' },
    ],

    legal: APP_COMPANY.COPYRIGHT,

    showBrandLS: APP_COMPANY_CONFIG_LS.HAS.HAS__FOOTER_BRAND_POWERED_LS === true,

    /*
        🔥 PROTECCIÓN DE RUTAS

        ⚠️ ESTO ERA Load/AppLoadGuards.js

        El interruptor está en Plugins/Apply.Plugin > APPLY__ROUTE_GUARD.
        Acá viven las RUTAS, porque este archivo ya es la fuente de verdad
        del routing y tenerlas en dos lugares es garantía de que se van a
        desincronizar.

        ⚠️ forbidden NO ES EL LOGIN. Quien llega ahí YA tiene sesión: lo
           que le falta es el permiso. Mandarlo al login sugiere que el
           problema es la sesión, y no lo es.
    */
    guards: {
        isEnabled: CONFIG_PLUGIN?.ROUTING?.HAS__ROUTE_GUARD ?? false,
        loginPath: '/ingresar',
        forbiddenPath: '/sin-permiso',
        afterLoginPath: '/dashboard',
        guestOnlyPaths: ['/ingresar'],
    },

    isSeedActive: IS_SEED_ACTIVE,
};

/*
    ---------------------------- 🔥 NAVEGACIÓN CON SESIÓN

    Lo mismo que APP_SHELL_MENU.publicNav / .privateNav, pero resuelto
    CON el usuario. Se llama dentro del componente, donde la sesión ya
    existe.

    ⚠️ CUÁNDO USAR CADA UNO

    APP_SHELL_MENU.publicNav   sirve para rutas y etiquetas: no cambian
                               según quién mire.
    onResolveShellNav(user)    hace falta cuando el módulo se arma según
                               el usuario. Tu HomeModule ya lo hace: con
                               isServerDown deja scrollToItems vacío.

    ⚠️ NO FILTRA POR PERMISOS. Devuelve todo y el layout decide con
       userHasAccess. Filtrar acá escondería rutas que el router
       igualmente resuelve, y eso da la falsa sensación de estar
       protegido.

    ⚠️ CON EL SEED ENCENDIDO devuelve el seed y el usuario se ignora.
       Es a propósito: el seed son datos fijos de ejemplo.
*/
export const onResolveShellNav = (dataUser = null, lang = 'es') => {

    const SHELL_TEXT = onShellContent(lang);

    const FOOTER_GROUP = SHELL_TEXT.TEMPLATES.nav.defaultGroup;

    const PUBLIC_NAV = onResolveNavigation(
        APP_LOAD_DATA_MODE.LOAD_SEED_PUBLIC_NAVIGATION, APP_MODULES_GET_PUBLIC_ROUTES, dataUser, lang,
    );

    const PRIVATE_NAV = onResolveNavigation(
        APP_LOAD_DATA_MODE.LOAD_SEED_PRIVATE_NAVIGATION, APP_MODULES_GET_PRIVATE_ROUTES, dataUser, lang,
    );

    /*
        ⚠️ publicNav SE FILTRA, publicRoutes NO

        Son dos cosas distintas y confundirlas fue el error:
          publicRoutes -> TODAS. El router tiene que registrar hasta las
                          que nadie enlaza (/auth/verify-code, /blog/:slug).
          publicNav    -> sólo displayInHeader. Es lo que se dibuja.

        Sin este filtro, los items con label vacío entran igual al header
        y se ven como huecos entre los enlaces de verdad.
    */
    return {
        publicRoutes: PUBLIC_NAV,
        privateRoutes: PRIVATE_NAV,
        publicNav: PUBLIC_NAV.filter((item) => item.displayInHeader),
        privateNav: PRIVATE_NAV,
        footerColumns: onBuildFooterColumns(PUBLIC_NAV, FOOTER_GROUP),
        footerColumnsPrivate: onBuildFooterColumns(PRIVATE_NAV, FOOTER_GROUP),
        claim: SHELL_TEXT.COMPANY.CLAIM,
        legal: `© ${APP_COMPANY.COMPANY_NAME} - ${APP_COMPANY.COMPANY_YEARS}. ${SHELL_TEXT.COMPANY.RIGHTS}`,
        portalLabel: SHELL_TEXT.TEMPLATES.header.portal,
        sidebarGroup: SHELL_TEXT.TEMPLATES.nav.sidebarGroup,
        profileLinks: [
            { path: '/dashboard', label: SHELL_TEXT.TEMPLATES.header.myPanel },
            { path: '/dashboard/perfil', label: SHELL_TEXT.TEMPLATES.header.myProfile },
        ],
    };
};
